import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { GroupProvider, useGroups } from "@/context/GroupContext";
import { I18nProvider } from "@/context/I18nContext";
import { TextStyleProvider } from "@/context/TextStyleContext";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import AppLayout from "@/components/AppLayout";
import CouplesPage from "@/pages/CouplesPage";
import BirdsPage from "@/pages/BirdsPage";
import GroupsLandingPage from "@/pages/GroupsLandingPage";
import PedigreePage from "@/pages/PedigreePage";
import CoursesPage from "@/pages/CoursesPage";
import SubscriptionPage from "@/pages/SubscriptionPage";
import SettingsPage from "@/pages/SettingsPage";
import ReferralPage from "@/pages/ReferralPage";
import MarketPage from "@/pages/MarketPage";
import MarketListPage from "@/pages/MarketListPage";
import MarketMinePage from "@/pages/MarketMinePage";
import MarketListingPage from "@/pages/MarketListingPage";
import MarketFormPage from "@/pages/MarketFormPage";
import RemarksPage from "@/pages/RemarksPage";
import FeedbackPage from "@/pages/FeedbackPage";
import ReviewsPage from "@/pages/ReviewsPage";
import MessagesPage from "@/pages/MessagesPage";
import AdminActivityPage from "@/pages/AdminActivityPage";
import AnnouncementPage from "@/pages/AnnouncementPage";
import AdminSubscriptionsPage from "@/pages/AdminSubscriptionsPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import AuthCallback from "@/pages/AuthCallback";
import { SubscriptionProvider } from "@/context/SubscriptionContext";
import { Toaster } from "sonner";
import { Loader2 } from "lucide-react";

function ProtectedRoute() {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="min-h-screen grid place-items-center bg-cream">
        <Loader2 className="w-6 h-6 animate-spin text-olive" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return <Outlet />;
}

function RequireGroup() {
  const { groups, activeId } = useGroups();
  if (groups === null) {
    return (
      <div className="min-h-screen grid place-items-center bg-cream">
        <Loader2 className="w-6 h-6 animate-spin text-olive" />
      </div>
    );
  }
  if (groups.length === 0 || !activeId) return <Navigate to="/groups" replace />;
  return <Outlet />;
}

function PublicRoute() {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="min-h-screen grid place-items-center bg-cream">
        <Loader2 className="w-6 h-6 animate-spin text-olive" />
      </div>
    );
  }
  if (user) return <Navigate to="/couples" replace />;
  return <Outlet />;
}

function AppRouter() {
  const location = useLocation();
  // CRITICAL: Detect `session_id` in the URL fragment synchronously (NOT in useEffect)
  // so we process the OAuth callback BEFORE ProtectedRoute can run.
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH.
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route element={<PublicRoute />}>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
      </Route>
      <Route element={<ProtectedRoute />}>
        <Route path="/subscription" element={<SubscriptionPage />} />
        <Route path="/admin/subscriptions" element={<AdminSubscriptionsPage />} />
        <Route path="/groups" element={<GroupsLandingPage />} />
        <Route element={<RequireGroup />}>
          <Route element={<AppLayout />}>
            <Route path="/" element={<Navigate to="/couples" replace />} />
            <Route path="/couples" element={<CouplesPage />} />
            <Route path="/birds" element={<BirdsPage />} />
            <Route path="/pedigree" element={<PedigreePage />} />
            <Route path="/pedigree/:ring" element={<PedigreePage />} />
            <Route path="/courses" element={<CoursesPage />} />
            <Route path="/remarks" element={<RemarksPage />} />
            <Route path="/market" element={<MarketPage />} />
            <Route path="/market/mine" element={<MarketMinePage />} />
            <Route path="/market/listing/:id" element={<MarketListingPage />} />
            <Route path="/market/listing/:id/edit" element={<MarketFormPage />} />
            <Route path="/market/:type/new" element={<MarketFormPage />} />
            <Route path="/market/:type" element={<MarketListPage />} />
            <Route path="/reviews" element={<ReviewsPage />} />
            <Route path="/messages" element={<MessagesPage />} />
            <Route path="/admin/activity" element={<AdminActivityPage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/referrals" element={<ReferralPage />} />
            <Route path="/feedback" element={<FeedbackPage />} />
            <Route path="/announcement" element={<AnnouncementPage />} />
          </Route>
        </Route>
      </Route>
      <Route path="*" element={<Navigate to="/couples" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <I18nProvider>
          <TextStyleProvider>
            <SubscriptionProvider>
              <GroupProvider>
                <BrowserRouter>
                  <AppRouter />
                </BrowserRouter>
                <Toaster position="top-right" richColors />
              </GroupProvider>
            </SubscriptionProvider>
          </TextStyleProvider>
        </I18nProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
